import { Box, Button, Icon, IconButton, Stack, StackProps, Typography } from '@mui/material';
import {
  CustomFieldApplicableTo,
  DATE_FORMAT_RANGE_SHORT_FNS,
  DATE_TIME_WITH_DASH_FORMAT_FNS,
  ExportStudentsParams,
  ExportStudentsSort,
  FilterKeys,
  GetStudentsQueryFilters,
  GetStudentsQuerySort,
  StudentExportIncludeEnum,
  useExportStudents,
  useGetCustomFieldsForSchoolQuery,
} from '@schooly/api';
import { toggleArrayProperty } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import {
  GENDER_OPTIONS,
  NATIONALITY_OPTIONS,
  SchoolPropertyType,
  SchoolUserRole,
} from '@schooly/constants';
import { useFlag } from '@schooly/hooks/use-flag';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import {
  ActionButtonComponent,
  CheckboxSquareIconButton,
  CheckboxSquareLabel,
  CheckIcon,
  CrossIcon,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMain,
  ModalSmall,
  TruncatedTagSelect,
} from '@schooly/style';
import { findItemByPath, getTypedObjectEntries } from '@schooly/utils/common';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { isNotEmpty } from '@schooly/utils/predicates';
import { format } from 'date-fns';
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';
import { downloadFile } from '../../utils/downloadFile';

export const StudentExportIncludeTranslations = Object.entries(StudentExportIncludeEnum).reduce<
  Record<string, string>
>((acc, [key, value]) => ({ ...acc, [value]: `students-${key}` }), {});

export const useStudentsExport = (exportButtonRef: React.RefObject<ActionButtonComponent>) => {
  const [opened, open, close] = useFlag();
  const { showError } = useNotifications();

  const { isLoading, mutateAsync: exportStudents } = useExportStudents();

  const handleExport = useCallback(
    async (params: ExportStudentsParams) => {
      close();

      const exportPromise = exportStudents(params, {
        onSuccess: (data) => {
          try {
            const timestamp = format(new Date(), DATE_TIME_WITH_DASH_FORMAT_FNS);
            downloadFile(data, `students_${timestamp}.csv`);
          } catch (err) {
            console.error('Failed to download file:', err);
          }
        },
        onError: showError,
      });

      exportButtonRef.current?.handleAction(exportPromise);
    },
    [close, exportStudents, showError, exportButtonRef],
  );

  return {
    opened,
    open,
    close,
    handleExport,
    isLoading,
  };
};

const exportSections = [
  {
    fields: [
      StudentExportIncludeEnum.GivenName,
      StudentExportIncludeEnum.LastName,
      StudentExportIncludeEnum.DateOfBirth,
      StudentExportIncludeEnum.Nationality,
    ],
    labelId: 'students-StudentsDetails',
  },
  {
    fields: [
      StudentExportIncludeEnum.Id,
      StudentExportIncludeEnum.AgeGroup,
      StudentExportIncludeEnum.Status,
      StudentExportIncludeEnum.StatusAppliesFrom,
    ],
    labelId: 'students-CurrentRegistration',
  },
  {
    fields: [
      StudentExportIncludeEnum.House,
      StudentExportIncludeEnum.TutorGroup,
      StudentExportIncludeEnum.Email,
    ],
    labelId: 'students-HouseAndAcademicDetails',
  },
  {
    fields: [
      StudentExportIncludeEnum.PrimaryContactGivenName,
      StudentExportIncludeEnum.PrimaryContactLastName,
      StudentExportIncludeEnum.PrimaryContactPhoneNumber,
      StudentExportIncludeEnum.PrimaryContactEmail,
    ],
    labelId: 'students-PrimaryContactDetails',
  },
  {
    fields: [
      StudentExportIncludeEnum.MedicalNeedsDescription,
      StudentExportIncludeEnum.MedicalNeedsSevere,
      StudentExportIncludeEnum.MedicalNeedsReviewStatus,
    ],
    labelId: 'students-MedicalDetails',
  },
] as const;

type ExportSection = (typeof exportSections)[number];

const requiredLabels: Array<ExportSection['labelId']> = ['students-StudentsDetails'];

export type StudentsExportFormData = {
  selectedLabels: ExportSection['labelId'][];
  selectedCustomFields: string[];
};

type StudentsExportModalProps = {
  open: boolean;
  onClose: () => void;
  filters: GetStudentsQueryFilters;
  sort?: GetStudentsQuerySort;
  onExport: (params: ExportStudentsParams) => void;
};

export const StudentsExportModal: FC<StudentsExportModalProps> = ({
  open,
  onClose,
  filters,
  sort,
  onExport,
}) => {
  const form = useForm<StudentsExportFormData>({
    defaultValues: {
      selectedLabels: requiredLabels,
      selectedCustomFields: [],
    },
  });

  const exportSort: ExportStudentsSort[] | undefined = useMemo(() => {
    if (sort?.columnTextId === 'last_name') {
      return [sort, { columnTextId: 'given_name', direction: sort.direction }];
    }

    return sort ? [sort] : undefined;
  }, [sort]);

  const { schoolId } = useSchool();
  const selectedCustomFields = form.watch('selectedCustomFields');
  const selectedLabels = form.watch('selectedLabels');
  const { formatMessage } = useIntl();

  const { data: customFields } = useGetCustomFieldsForSchoolQuery(
    {
      schoolId: schoolId || '',
      filter: {
        applicable_to: [CustomFieldApplicableTo.Student],
      },
    },
    {
      refetchOnMount: 'always',
      enabled: !!schoolId,
    },
  );

  const studentCustomFields = useMemo(
    () => (customFields ? customFields.filter((field) => !field.archived) : []),
    [customFields],
  );

  const { activePropertiesMap } = useSchoolProperties({
    schoolId: schoolId || '',
    userType: SchoolUserRole.Student,
    showReEnrollmentProperties: true,
  });

  const filterStringArr = useMemo(() => {
    const stings = getTypedObjectEntries(filters).flatMap(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return '';
      switch (key) {
        case FilterKeys.Date:
          return format(newDateTimezoneOffset(value?.[0]), DATE_FORMAT_RANGE_SHORT_FNS);
        case FilterKeys.Status:
          const statuses = activePropertiesMap[SchoolPropertyType.Status];
          return value.flatMap((id) => findItemByPath(statuses, 'id', id)?.name);
        case FilterKeys.AgeGroup:
          const ageGroups = activePropertiesMap[SchoolPropertyType.AgeGroup];
          return value.flatMap((id) => findItemByPath(ageGroups, 'id', id)?.name);
        case FilterKeys.House:
          const houses = activePropertiesMap[SchoolPropertyType.House];
          return value.map((id) => findItemByPath(houses, 'id', id)?.name);
        case FilterKeys.Nationality:
          return value.map((id) => findItemByPath(NATIONALITY_OPTIONS, 'value', id)?.label);
        case FilterKeys.Gender:
          return value.map((id) => findItemByPath(GENDER_OPTIONS, 'value', id)?.label);
        default:
          console.error('Unhandled filter key', key, value);
          return '';
      }
    });
    return stings.filter((s) => !!s);
  }, [activePropertiesMap, filters]);

  const handleSubmit = useCallback<SubmitHandler<StudentsExportFormData>>(
    (data) => {
      onExport({
        schoolId: schoolId || '',
        filters,
        sort: exportSort,
        include: data.selectedLabels
          .flatMap((label) => exportSections.find((section) => section.labelId === label)?.fields)
          .filter(isNotEmpty),
        custom_field_ids: data.selectedCustomFields,
      });
    },
    [onExport, schoolId, filters, exportSort],
  );

  return (
    <ModalSmall open={open} onClose={onClose}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <ModalHeader active title={formatMessage({ id: 'students-ExportStudentData' })}>
            <IconButton onClick={onClose}>
              <CrossIcon />
            </IconButton>
          </ModalHeader>
          <ModalMain>
            <ModalContent active>
              <Stack gap={1} sx={{ '& > .MuiTypography-root': { color: 'common.grey2' }, pb: 2 }}>
                <Typography whiteSpace="nowrap">
                  {formatMessage({ id: 'students-FiltersApplied' })}:
                </Typography>
                <TruncatedTagSelect
                  spacing={1.75}
                  items={filterStringArr}
                  tooltipProps={{
                    componentsProps: { tooltip: { sx: { maxWidth: 'unset' } } },
                  }}
                />
              </Stack>
              <Stack gap={1.75}>
                {exportSections.map((section) => {
                  const isActive = selectedLabels.includes(section.labelId);
                  const isRequired = requiredLabels.includes(section.labelId);

                  return (
                    <Stack key={section.labelId} gap={1}>
                      {isRequired ? (
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Icon color="primary">
                            <CheckIcon />
                          </Icon>
                          <Typography variant="h3">
                            {formatMessage({ id: section.labelId })}
                          </Typography>
                        </Stack>
                      ) : (
                        <CheckboxWithLabel
                          label={formatMessage({ id: section.labelId })}
                          isActive={isActive}
                          onClick={() => {
                            form.setValue(
                              'selectedLabels',
                              toggleArrayProperty(selectedLabels, section.labelId),
                            );
                          }}
                        />
                      )}
                      <ListWrapper>
                        {section.fields.map((field) => (
                          <Stack key={field} direction="row" alignItems="center" gap={1}>
                            <Box
                              sx={(theme) => ({
                                width: theme.spacing(0.25),
                                height: theme.spacing(0.25),
                                backgroundColor: theme.palette.common.grey2,
                                borderRadius: '50%',
                              })}
                            />
                            <Typography>
                              {formatMessage({ id: StudentExportIncludeTranslations[field] })}
                            </Typography>
                          </Stack>
                        ))}
                      </ListWrapper>
                    </Stack>
                  );
                })}

                {studentCustomFields.length > 0 && (
                  <Box>
                    <Typography variant="h3" pb={1}>
                      {formatMessage({ id: 'peopleDetail-AdditionalInformation' })}
                    </Typography>
                    <ListWrapper gap={1}>
                      {studentCustomFields.map((field) => (
                        <CheckboxWithLabel
                          key={field.id}
                          label={field.label}
                          isActive={selectedCustomFields.includes(field.id)}
                          onClick={() => {
                            form.setValue(
                              'selectedCustomFields',
                              toggleArrayProperty(selectedCustomFields, field.id),
                            );
                          }}
                        />
                      ))}
                    </ListWrapper>
                  </Box>
                )}
              </Stack>
            </ModalContent>
          </ModalMain>
          <ModalFooter active>
            <Button type="submit">{formatMessage({ id: 'students-ExportAsCSV' })}</Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </ModalSmall>
  );
};

const ListWrapper: FC<PropsWithChildren & StackProps> = ({ children, ...props }) => {
  return (
    <Stack
      sx={(theme) => ({
        border: theme.mixins.borderValue(),
        borderRadius: theme.spacing(1),
        py: theme.spacing(1),
        px: theme.spacing(1.75),
        backgroundColor: theme.palette.common.lightBg,
      })}
      {...props}
    >
      {children}
    </Stack>
  );
};

type CheckboxWithLabelProps = {
  label: string;
  isActive: boolean;
  onClick: () => void;
};

const CheckboxWithLabel: FC<CheckboxWithLabelProps> = ({ label, isActive, onClick }) => {
  return (
    <CheckboxSquareLabel
      key={`${label}-${isActive}`}
      active={isActive}
      control={<CheckboxSquareIconButton isActive={isActive} />}
      label={
        <Typography variant="h3" color="inherit">
          {label}
        </Typography>
      }
      onClick={onClick}
    />
  );
};
